/**
 * Tracks the "user just downloaded" signal so the install guide can fire
 * `guide_visited_post_download` when the same session lands on /guide.
 *
 * Stored in sessionStorage so it auto-expires when the tab closes.
 */

const KEY = 'tls_last_download_v1';

export interface DownloadAttribution {
  sound_id: string;
  sound_title?: string;
  source?: string;
  /** epoch ms */
  ts: number;
}

export function markDownloadCompleted(attr: Omit<DownloadAttribution, 'ts'>) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ ...attr, ts: Date.now() } satisfies DownloadAttribution),
    );
  } catch {
    // sessionStorage can throw in private mode / quota-exceeded; safe to ignore.
  }
}

/** Returns the attribution and clears it (one-shot consumption). */
export function consumeDownloadAttribution(): DownloadAttribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as DownloadAttribution;
    if (!parsed || typeof parsed.ts !== 'number') return null;
    // 30-minute attribution window
    if (Date.now() - parsed.ts > 30 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Peek without consuming — used by the install confirmation CTA. */
function peekDownloadAttribution(): DownloadAttribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DownloadAttribution;
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > 30 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}
